import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './OvertimeDashboard.module.css';
import { useNavigate } from 'react-router-dom';

const OvertimeDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const today = new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [driverFilter, setDriverFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  // Set default dates to current day on component load
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://anltcs.noorride.com/overtime-data?startdate=${startDate}&enddate=${endDate}`
        );
        setData(response.data);
        setFilteredData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  console.log(data);

  // Filter data based on driver and vehicle
  useEffect(() => {
    const filtered = Object.keys(data).reduce((acc, key) => {
      const driver = data[key];
      const matchesDriver = driver.driver_name.toLowerCase().includes(driverFilter.toLowerCase());
      const matchesVehicle = driver.records.some(record =>
        record.license_plate.toLowerCase().includes(vehicleFilter.toLowerCase())
      );
      if (matchesDriver && matchesVehicle) {
        acc[key] = driver;
      }
      return acc;
    }, {});
    setFilteredData(filtered);
  }, [driverFilter, vehicleFilter, data]);

  // Toggle row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Driver Name',
      'Phone',
      'Total Overtime',
      'License Plate',
      'Date',
      'Overtime Hours',
      'Shift Start',
      'Shift End',
      'Working Hours',
      'Login Time',
      'Logout Time',
    ];
    const rows = Object.keys(filteredData).flatMap(key => {
      const driver = filteredData[key];
      return driver.records.map(record => [
        driver.driver_name,
        driver.phone,
        driver.total_overtime,
        record.license_plate,
        record.date,
        record.overtime_hours,
        record.shift_start,
        record.shift_end,
        record.working_hours,
        record.login,
        record.logout,
      ]);
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'overtime_data.csv';
    link.click();
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: '#1e40af',
            color: 'white',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>
      <h1 className={styles.title}>Overtime Dashboard</h1>

      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.filterItem}>
          <label className={styles.label}>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.filterItem}>
          <label className={styles.label}>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.filterItem}>
          <label className={styles.label}>Driver Name</label>
          <input
            type="text"
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            placeholder="Filter by driver name"
            className={styles.input}
          />
        </div>
        <div className={styles.filterItem}>
          <label className={styles.label}>Vehicle Plate</label>
          <input
            type="text"
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            placeholder="Filter by license plate"
            className={styles.input}
          />
        </div>
        <div className={styles.filterButton}>
          <button onClick={exportToCSV} className={styles.csvButton}>
            Export to CSV
          </button>
        </div>
      </div>

      {/* Summary Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.th}>Driver Name</th>
              <th className={styles.th}>Phone</th>
              <th className={styles.th}>Total Overtime</th>
              <th className={styles.th}>License Plate</th>
              <th className={styles.th}>Login Time</th>
              <th className={styles.th}>Logout Time</th>
              <th className={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(filteredData).map(key => (
              <React.Fragment key={key}>
                <tr className={styles.tr}>
                  <td className={styles.td}>{filteredData[key].driver_name}</td>
                  <td className={styles.td}>{filteredData[key].phone}</td>
                  <td className={styles.td}>{filteredData[key].total_overtime}</td>
                  <td className={styles.td}>
                    {[...new Set(filteredData[key].records.map(r => r.license_plate))].join(', ')}
                  </td>
                  <td className={styles.td}>
                    {filteredData[key].records[0]?.login || '-'}
                  </td>
                  <td className={styles.td}>
                    {filteredData[key].records[0]?.logout || '-'}
                  </td>
                  <td className={styles.td}>
                    <button
                      onClick={() => toggleRow(key)}
                      className={styles.detailsButton}
                    >
                      {expandedRows[key] ? 'Hide Details' : 'Show Details'}
                    </button>
                  </td>
                </tr>
                {expandedRows[key] && (
                  <tr>
                    <td colSpan="7" className={styles.detailsTd}>
                      <table className={styles.detailsTable}>
                        <thead className={styles.detailsThead}>
                          <tr>
                            <th className={styles.detailsTh}>Date</th>
                            <th className={styles.detailsTh}>Overtime Hours</th>
                            <th className={styles.detailsTh}>Shift Start</th>
                            <th className={styles.detailsTh}>Shift End</th>
                            <th className={styles.detailsTh}>Working Hours</th>
                            <th className={styles.detailsTh}>License Plate</th>
                            <th className={styles.detailsTh}>Login Time</th>
                            <th className={styles.detailsTh}>Logout Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData[key].records.map((record, index) => (
                            <tr key={index} className={styles.detailsTr}>
                              <td className={styles.detailsTd}>{record.date}</td>
                              <td className={styles.detailsTd}>{record.overtime_hours}</td>
                              <td className={styles.detailsTd}>{record.shift_start}</td>
                              <td className={styles.detailsTd}>{record.shift_end}</td>
                              <td className={styles.detailsTd}>{record.working_hours}</td>
                              <td className={styles.detailsTd}>{record.license_plate}</td>
                              <td className={styles.detailsTd}>{record.login ? record.login : '-'}</td>
                              <td className={styles.detailsTd}>{record.logout ? record.logout : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OvertimeDashboard;